import React from 'react'
import type { AppError } from '../hooks/useTournamentConnection'
import './error-card.css'

interface ErrorCardProps {
  error: AppError
  onRetry?: () => void
}

/**
 * Maps error types to user-friendly titles and descriptions.
 */
const getErrorDisplay = (error: AppError) => {
  const errorTypeMap: Record<string, { title: string; description: string }> = {
    'ConnectionError': {
      title: 'Unable to Connect',
      description: 'Could not establish connection to the tournament server. Check your internet connection and try again.',
    },
    'AuthenticationError': {
      title: 'Authentication Failed',
      description: 'Your session has expired or credentials are invalid. Please rejoin the tournament.',
    },
    'TimeoutError': {
      title: 'Connection Timeout',
      description: 'The server took too long to respond. Please check your connection and try again.',
    },
    'RequestError': {
      title: 'Request Failed',
      description: 'The server returned an error. Please try again.',
    },
    'Error': {
      title: 'Something Went Wrong',
      description: 'An unexpected error occurred. Please refresh the page and try again.',
    },
  }

  const errorInfo = errorTypeMap[error.type] || errorTypeMap['Error']

  return {
    ...errorInfo,
    // Include the technical error message for debugging (shown in console only)
    technicalMessage: error.message,
  }
}

export const ErrorCard: React.FC<ErrorCardProps> = ({ error, onRetry }) => {
  const errorDisplay = getErrorDisplay(error)

  const handleRetry = () => {
    // Reload the page to restart the widget initialization
    window.location.reload()
  }

  return (
    <div className="error-card-container">
      <div className="error-card">
        <div className="error-card__icon">⚠️</div>
        <h2 className="error-card__title">{errorDisplay.title}</h2>
        <p className="error-card__description">{errorDisplay.description}</p>
        <button className="error-card__button" onClick={handleRetry}>
          Try Again
        </button>
        <div className="error-card__tech-info">
          <small>{errorDisplay.technicalMessage}</small>
        </div>
      </div>
    </div>
  )
}
